import { Component } from '@theme/component';

// Touch devices get the lightest rendition Shopify has for the file (see
// #pickSource); pointer devices keep the browser's own <source> selection.
const TOUCH_PRIMARY = window.matchMedia?.('(hover: none)').matches ?? false;

// How far outside the viewport a video is still considered "near": the
// <video> is created (and its download starts) only inside this zone, and it
// pauses once it leaves it. One viewport of margin keeps the next section's
// video ready by the time the user scrolls to it.
const NEAR_MARGIN = '100% 0px';

// Smallest rendition that still looks sharp on a phone (390 px wide @3x ≈ 1170
// px): the first one at or above this height wins; if none, the largest below.
const TOUCH_MIN_HEIGHT = 720;

// play() refused for lack of a user gesture: iOS Low Power Mode does this even
// for muted, inline video (NotAllowedError), and the poster stays up. Each
// entry retries on the next tap / key press anywhere on the page, which iOS
// does allow; one set of document listeners serves every video on the page.
const GESTURE_EVENTS = ['touchend', 'click', 'keydown'];

/** @type {Map<HTMLVideoElement, () => void>} */
const awaitingGesture = new Map();

function retryAwaitingGesture() {
	for (const [video, retry] of awaitingGesture) {
		awaitingGesture.delete(video);
		if (video.isConnected) retry(); // synchronous: still inside the gesture
	}
	if (awaitingGesture.size === 0) {
		for (const type of GESTURE_EVENTS) document.removeEventListener(type, retryAwaitingGesture, true);
	}
}

/**
 * @param {HTMLVideoElement} video
 * @param {() => void} retry - Called synchronously inside the next user gesture
 */
function retryOnNextGesture(video, retry) {
	if (awaitingGesture.size === 0) {
		for (const type of GESTURE_EVENTS) {
			document.addEventListener(type, retryAwaitingGesture, { capture: true, passive: true });
		}
	}
	awaitingGesture.set(video, retry);
}

/**
 * @typedef {Object} ResponsiveVideoRefs
 * @property {HTMLTemplateElement} mobileTemplate - Mobile video template
 * @property {HTMLTemplateElement} desktopTemplate - Desktop video template
 * @property {HTMLElement} container - Video container element
 */

/**
 * Responsive video component that loads different videos based on viewport width.
 *
 * Features:
 * - Loads only the appropriate video (mobile or desktop) on initial render
 * - Swaps video when crossing breakpoint threshold
 * - Uses ResizeObserver for efficient viewport detection
 * - Creates the <video> only when the block is near the viewport, and pauses
 *   it while it is away: a page with several autoplay videos (the homepage has
 *   four) no longer downloads and decodes all of them up front. Measured
 *   before: the homepage hero alone pulled a 21.7 MB 1080p MP4 on a phone.
 * - On touch devices keeps a single <source>: HLS when present (Safari adapts
 *   the bitrate), otherwise the lightest MP4 rendition that is still ≥ 720p.
 *   The templates list every MP4 rendition, largest first
 *   (snippets/shared--basic--video--tag.liquid); with a single rendition this
 *   is a no-op.
 *
 * Layout (aspect ratio, full viewport height) is handled entirely by CSS media
 * queries rendered server-side in ui-video.liquid. This component only swaps
 * the <video> element for different sources.
 *
 * @example
 * <responsive-video data-breakpoint="768">
 *   <template ref="mobileTemplate">
 *     <video src="mobile.mp4" autoplay muted loop playsinline></video>
 *   </template>
 *   <template ref="desktopTemplate">
 *     <video src="desktop.mp4" autoplay muted loop playsinline></video>
 *   </template>
 *   <div ref="container" class="video-block__wrapper"></div>
 * </responsive-video>
 *
 * @extends {Component<ResponsiveVideoRefs>}
 */
class ResponsiveVideo extends Component {
	/** @type {ResizeObserver | null} */
	#resizeObserver = null;

	/** @type {IntersectionObserver | null} */
	#nearObserver = null;

	/** @type {'mobile' | 'desktop' | null} */
	#currentMode = null;

	/** Poster loaded (or not needed) — the bandwidth gate on mobile. */
	#posterReady = false;

	/** Block is inside the NEAR_MARGIN zone — the visibility gate. */
	#near = false;

	connectedCallback() {
		super.connectedCallback();

		// A single-video block provides only desktopTemplate (see the snippet's
		// non-responsive branch): same lifecycle, no breakpoint swap.
		if (!this.refs.desktopTemplate || !this.refs.container) {
			console.warn('ResponsiveVideo: Missing required elements');
			return;
		}

		// On mobile (bandwidth-constrained), defer video activation until the poster
		// image loads so the 28MB video doesn't compete for bandwidth with the LCP image.
		// On desktop, activate immediately — fast connections can handle parallel downloads.
		const isMobile = window.innerWidth < (parseInt(this.dataset.breakpoint, 10) || 768);
		const poster = this.refs.container.querySelector('[data-poster] img');
		if (isMobile && poster && !poster.complete) {
			const ready = () => this.#markPosterReady();
			poster.addEventListener('load', ready, { once: true });
			poster.addEventListener('error', ready, { once: true });
			setTimeout(ready, 5000); // Safety fallback
		} else {
			this.#posterReady = true;
		}

		// Visibility gate: no <video> until the block is near the viewport, and
		// pause/resume as it leaves/re-enters. Falls back to "always near" where
		// IntersectionObserver is unavailable.
		if ('IntersectionObserver' in window) {
			this.#nearObserver = new IntersectionObserver((entries) => {
				for (const entry of entries) {
					this.#near = entry.isIntersecting;
					this.#near ? this.#onNear() : this.#onAway();
				}
			}, { rootMargin: NEAR_MARGIN });
			this.#nearObserver.observe(this);
		} else {
			this.#near = true;
			this.#onNear();
		}
	}

	#markPosterReady() {
		if (this.#posterReady) return;
		this.#posterReady = true;
		if (this.#near) this.#activate();
	}

	#onNear() {
		if (this.#currentMode === null) {
			if (this.#posterReady) this.#activate();
			return;
		}
		const video = this.refs.container?.querySelector('video');
		if (video && video.hasAttribute('autoplay') && video.paused) {
			this.#play(video);
		}
	}

	/**
	 * Start playback. When the browser requires a user gesture first (iOS Low
	 * Power Mode), retry on the next one — only if the block is still near the
	 * viewport by then; otherwise #onNear asks again when it comes back.
	 * @param {HTMLVideoElement} video
	 */
	#play(video) {
		video.play().catch((error) => {
			if (error?.name !== 'NotAllowedError') return;
			retryOnNextGesture(video, () => {
				if (this.#near) this.#play(video);
			});
		});
	}

	#onAway() {
		this.refs.container?.querySelector('video')?.pause();
	}

	/**
	 * Initialize video rendering and viewport watching
	 */
	#activate() {
		if (this.#currentMode !== null) return; // Already activated

		this.#updateVideo();

		// Watch for viewport changes
		this.#resizeObserver = new ResizeObserver(() => {
			this.#updateVideo();
		});
		this.#resizeObserver.observe(document.documentElement);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#resizeObserver?.disconnect();
		this.#nearObserver?.disconnect();
		this.#pauseCurrentVideo();
	}

	/**
	 * Get the breakpoint in pixels
	 * @returns {number}
	 */
	get breakpoint() {
		return parseInt(this.dataset.breakpoint || '768', 10);
	}

	/**
	 * Determine if we should show mobile or desktop video
	 * @returns {'mobile' | 'desktop'}
	 */
	#getTargetMode() {
		if (!this.refs.mobileTemplate) return 'desktop';
		return window.innerWidth < this.breakpoint ? 'mobile' : 'desktop';
	}

	/**
	 * Update the video if mode has changed
	 */
	#updateVideo() {
		const targetMode = this.#getTargetMode();

		if (targetMode === this.#currentMode) {
			return;
		}

		this.#pauseCurrentVideo();
		this.#renderVideo(targetMode);
		this.#currentMode = targetMode;
	}

	/**
	 * Pause and clean up the current video
	 */
	#pauseCurrentVideo() {
		const video = this.refs.container?.querySelector('video');
		if (video) {
			video.pause();
			video.removeAttribute('src');
			video.load();
		}
	}

	/**
	 * Touch devices: keep one <source>. HLS first when present (Safari picks
	 * the bitrate for the connection); otherwise the lightest MP4 rendition at
	 * or above TOUCH_MIN_HEIGHT, reading the height Shopify encodes in the
	 * filename ("…HD-720p-4.5Mbps….mp4"). Pointer devices are left alone.
	 * @param {HTMLVideoElement} video
	 */
	#pickSource(video) {
		if (!TOUCH_PRIMARY) return;
		const sources = [...video.querySelectorAll('source')];
		if (sources.length < 2) return;

		const hls = sources.find((s) => /mpegurl/i.test(s.type) || /\.m3u8(\?|$)/i.test(s.src));
		let keep = hls;
		if (!keep) {
			const ranked = sources
				.map((s) => ({ s, h: parseInt((/-(\d{3,4})p-/.exec(s.src) || [])[1], 10) || Infinity }))
				.sort((a, b) => a.h - b.h);
			keep = (ranked.find((r) => r.h >= TOUCH_MIN_HEIGHT) || ranked[ranked.length - 1]).s;
		}
		for (const s of sources) {
			if (s !== keep) s.remove();
		}
	}

	/**
	 * Render the video for the given mode
	 * @param {'mobile' | 'desktop'} mode
	 */
	#renderVideo(mode) {
		const { container } = this.refs;
		if (!container) return;

		const template = mode === 'mobile' ? this.refs.mobileTemplate : this.refs.desktopTemplate;
		if (!template) return;

		// Remove existing video but preserve poster
		const existingVideo = container.querySelector('video');
		if (existingVideo) {
			existingVideo.remove();
		}
		const content = template.content.cloneNode(true);
		const pending = content.querySelector('video');
		if (pending) this.#pickSource(pending);
		container.appendChild(content);

		// Ensure video starts playing (for autoplay videos)
		const video = container.querySelector('video');
		if (video && video.hasAttribute('autoplay')) {
			this.#play(video);
		}

		// Fade out the poster once the video is playing — and only if the video
		// is actually rendered: hiding the poster over a video that some CSS
		// hides would leave the block blank.
		const poster = container.querySelector('[data-poster]');
		if (poster && video) {
			const hidePoster = () => {
				if (video.getClientRects().length === 0) return;
				poster.classList.add('is-hidden');
				video.removeEventListener('playing', hidePoster);
			};
			video.addEventListener('playing', hidePoster);
		}
	}
}

// Register the custom element
if (!customElements.get('responsive-video')) {
	customElements.define('responsive-video', ResponsiveVideo);
}
